import {mapObject, objectFromEntries, groupBy} from '@benchling/js-utils';
import React from 'react';

import {ModalMetadataEntryType} from 'analytics/AnalyticsTypes';
import {CICOItemIdsToAssignee} from 'constants/SampleStatusConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {TargetStorableNoIcon} from 'notebook/structured-tables/action-functions/inventoryShortcutsActionFunction';
import {PartySummary} from 'parties/PartyConstants';
import {
  ProcessTasksCreatorsQuery,
  ProcessTasksCreatorsQueryVariables,
} from 'processes/process-task-details/graphql/__generated/ProcessTasksCreatorsQuery';
import {PROCESS_TASK_CREATORS_QUERY} from 'processes/process-task-details/graphql/ProcessTasksCreators';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {listToReadable} from 'util/NameUtil';

import BaseCheckOutModal, {BaseCheckoutModalProps} from './BaseCheckOutModal';

type ProcessTask = ExtractByTypename<ProcessTasksCreatorsQuery['processTasks'][number], 'ProcessTaskNode'>;

export interface TargetTaskIdAndStorables {
  targetStorables: Array<TargetStorableNoIcon>;
  taskId: string;
}

export interface BaseCheckoutFromProcessTableModalProps extends Omit<
  BaseCheckoutModalProps,
  'items' | 'title' | 'alternateAssigneeConfig'
> {
  targetTaskIdsAndStorables: Array<TargetTaskIdAndStorables>;
}

interface Props extends BaseCheckoutFromProcessTableModalProps {
  title: string;
  actionVerb: string;
}

function createProcessTaskAndCreatorMappings(processTasks: Array<ProcessTask>): {
  taskIdToCreator: Map<string, PartySummary>;
  creatorIdToCreator: Map<string, PartySummary>;
} {
  const taskIdToCreator: Map<string, PartySummary> = new Map();
  const creatorIdToCreator: Map<string, PartySummary> = new Map();
  for (const processTask of processTasks) {
    const creator = processTask.rootTask?.creator ?? processTask.creator;
    if (creator.__typename === 'UserNode') {
      taskIdToCreator.set(processTask.id, creator as unknown as PartySummary);
      creatorIdToCreator.set(creator.id, creator as unknown as PartySummary);
    }
  }
  return {taskIdToCreator, creatorIdToCreator};
}

/*
 * Returns mapping of storables to valid task creator, and items that cannot be checked out to task creator.
 *
 * @returns
 * - validAssigneeByItemId: mapping from item to assignee (task creator) to be used when checking out
 * - itemIdsWithMultipleAssignees: items that are linked to multiple tasks with different creators, and
 *   thus cannot be checked out to a task creator
 * - itemIdsWithoutValidAssignee: items that are linked to tasks without valid (user) creators
 * - storableIdToTargetStorable: mapping from item id to full target storable (with name)
 */
function parseProcessTasksToAssigneeMapping(
  processTasks: Array<ProcessTask>,
  targetTaskIdsAndStorables: Array<TargetTaskIdAndStorables>
): {
  validAssigneeByItemId: CICOItemIdsToAssignee;
  itemIdsWithMultipleAssignees: Set<string>;
  itemIdsWithoutValidAssignee: Set<string>;
  storableIdToTargetStorable: Map<string, TargetStorableNoIcon>;
} {
  const {taskIdToCreator, creatorIdToCreator} = createProcessTaskAndCreatorMappings(processTasks);
  const targetStorablesWithTaskCreators = targetTaskIdsAndStorables.flatMap(({targetStorables, taskId}) =>
    targetStorables.map((targetStorable) => ({targetStorable, taskCreator: taskIdToCreator.get(taskId)}))
  );
  const storableIdToTargetStorable = new Map(
    targetStorablesWithTaskCreators.map(({targetStorable}) => [targetStorable.id, targetStorable])
  );

  const assigneeIdsByItemId = mapObject(
    groupBy(targetStorablesWithTaskCreators, ({targetStorable}) => targetStorable.id),
    (taskCreatorsByStorableId) =>
      new Set(
        taskCreatorsByStorableId.flatMap(({taskCreator}) => (taskCreator != null ? [taskCreator.id] : []))
      )
  );
  let validAssigneeByItemId = objectFromEntries(
    Object.entries(assigneeIdsByItemId)
      .filter(([_, assigneeIds]) => assigneeIds.size === 1)
      .map(([itemId, [assigneeId]]) => [itemId, creatorIdToCreator.get(assigneeId)!])
  );

  const itemIdsWithMultipleAssignees = new Set(
    Object.entries(assigneeIdsByItemId)
      .filter(([_, assignees]) => assignees.size > 1)
      .map(([itemId, _]) => itemId)
  );
  const itemIdsWithoutValidAssignee = new Set(
    targetStorablesWithTaskCreators
      .filter(({taskCreator}) => taskCreator == null)
      .map(({targetStorable}) => targetStorable.id)
  );

  // Some items might be linked both to a task with a valid creator *and* to task(s) with invalid creators.
  // These will be in validAssigneeByItemId because they only have one valid task creator id. They should
  // be removed from the valid mapping, and added to the set of items with multiple assignees.
  for (const id of itemIdsWithoutValidAssignee) {
    if (validAssigneeByItemId.hasOwnProperty(id)) {
      const {[id]: targetEntry, ...rest} = validAssigneeByItemId;
      validAssigneeByItemId = rest;
      itemIdsWithMultipleAssignees.add(id);
    }
  }
  return {
    validAssigneeByItemId,
    itemIdsWithMultipleAssignees,
    itemIdsWithoutValidAssignee,
    storableIdToTargetStorable,
  };
}

/*
 * Wrapper component to check out items from a process table using task creator.
 *
 * Only supports tasks where the task itself or its root task have a user creator, since storables can only
 * be checked out to users or teams. Storables linked to multiple tasks with *different* task creators also
 * cannot be checked out via task creator.
 *
 * All storables can still be checked out with a manually selected assignee.
 */
export default function BaseCheckOutFromProcessTableModal(props: Props): JSX.Element | null {
  const result = useBenchlingQuery<ProcessTasksCreatorsQuery, ProcessTasksCreatorsQueryVariables>(
    PROCESS_TASK_CREATORS_QUERY,
    {
      variables: {processTaskIds: props.targetTaskIdsAndStorables.map(({taskId}) => taskId)},
    }
  );
  switch (result.type) {
    case QueryResultType.SUCCESS: {
      const processTasks = accessibleObjs(result.data.processTasks);
      const {
        validAssigneeByItemId,
        itemIdsWithMultipleAssignees,
        itemIdsWithoutValidAssignee,
        storableIdToTargetStorable,
      } = parseProcessTasksToAssigneeMapping(processTasks, props.targetTaskIdsAndStorables);

      const getInvalidAssigneesWarning = (selectedItemIds: Set<string>): JSX.Element | null => {
        const selectedItemIdsWithMultipleAssignees = [...itemIdsWithMultipleAssignees].filter((id) =>
          selectedItemIds.has(id)
        );
        const selectedItemIdsWithoutValidAssignees = [...itemIdsWithoutValidAssignee].filter((id) =>
          selectedItemIds.has(id)
        );
        const baseWarningText = `Some items can't be ${props.actionVerb} because they're linked`;
        const multipleAssigneeWarning = `${baseWarningText} to multiple tasks with different task creators: ${listToReadable(
          selectedItemIdsWithMultipleAssignees.map((id) => storableIdToTargetStorable.get(id)!.name)
        )}.`;
        const noAssigneeWarning = `${baseWarningText} to tasks without user task creators: ${listToReadable(
          selectedItemIdsWithoutValidAssignees.map((id) => storableIdToTargetStorable.get(id)!.name)
        )}.`;
        return selectedItemIdsWithMultipleAssignees.length > 0 ||
          selectedItemIdsWithoutValidAssignees.length > 0 ? (
          <div className="u-small-spacing-top">
            {selectedItemIdsWithMultipleAssignees.length > 0 && (
              <InlineBanner bannerType={InlineBannerType.WARNING} text={multipleAssigneeWarning} />
            )}
            {selectedItemIdsWithoutValidAssignees.length > 0 && (
              <InlineBanner bannerType={InlineBannerType.WARNING} text={noAssigneeWarning} />
            )}
          </div>
        ) : null;
      };

      return (
        <BaseCheckOutModal
          items={[...storableIdToTargetStorable.keys()].map((id) => ({id}))}
          onCheckOut={props.onCheckOut}
          onRequestClose={props.onRequestClose}
          title={props.title}
          statusWarningTrackingParams={props.statusWarningTrackingParams}
          alternateAssigneeConfig={{
            label: 'Task creator',
            validAssigneeByItemId,
            getWarning: getInvalidAssigneesWarning,
            analyticsMetadataEntryType: ModalMetadataEntryType.AUTO_POPULATED_FROM_PROCESS_TASK,
          }}
          analyticsMetadata={props.analyticsMetadata}
          shouldAlwaysShowSampleStatusTable={props.shouldAlwaysShowSampleStatusTable}
        />
      );
    }
    default:
      return null;
  }
}
